// Pass 5.2 — full per-workspace diagnostic card. Used by the
// /status/:teamId detail page; same primitive Pass 5.1 used
// inside the per-workspace card grid (now decommissioned in
// favour of the compact list view + this dedicated detail page).
//
// Design language is Pass 5.1's, unchanged:
//   * bg-theme-bg-card border-theme-border-default px-5 py-4
//   * Status dot + serif name + bordered vault pill
//   * Small-caps mono dt's + dense mono dd's in a 2-col grid
//   * Error callout in bg-theme-error-bg + border-theme-error-border
//   * Timestamps: relative time + absolute IST stacked inline
//     (Pass 5.2: IST instead of UTC — see lib/timeFmt.ts)

import type {
  SystemStatusWorkspace,
  DriveQueueDepth,
} from "../lib/api";
import {
  workspaceHealthColor,
  type HealthColor,
} from "../lib/workspaceHealth";
import { formatAbsoluteIst, formatRelative } from "../lib/timeFmt";
import { useAuth } from "../state/AuthContext";

const DOT_COLOR_CLASS: Record<HealthColor, string> = {
  green: "bg-theme-success-text",
  yellow: "bg-theme-warning-text",
  red: "bg-theme-error-border",
  gray: "bg-theme-text-tertiary",
};

const VAULT_PILL_TEXT_CLASS: Record<string, string> = {
  active: "text-theme-success-text",
  needs_reauth: "text-theme-error-text",
  disabled: "text-theme-error-text",
};

interface WorkspaceDetailCardProps {
  workspace: SystemStatusWorkspace;
  driveQueue: DriveQueueDepth | null;
}

export function WorkspaceDetailCard({
  workspace: w,
  driveQueue,
}: WorkspaceDetailCardProps) {
  const { role } = useAuth();
  const isSuperadmin = role === "superadmin";
  const color = workspaceHealthColor({ workspace: w, driveQueue });
  const vaultClass =
    VAULT_PILL_TEXT_CLASS[w.vault_status] ?? "text-theme-text-secondary";

  return (
    <article
      data-testid={`workspace-detail-${w.team_id}`}
      className="bg-theme-bg-card border border-theme-border-default px-5 py-4"
    >
      <div className="flex items-start gap-3">
        <span
          data-testid={`status-dot-${w.team_id}`}
          data-color={color}
          className={`mt-1.5 inline-block w-2 h-2 rounded-full flex-shrink-0 ${DOT_COLOR_CLASS[color]}`}
          aria-label={`${w.team_name} status: ${color}`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 flex-wrap">
            <h2 className="text-h3">
              {w.team_name}
            </h2>
            <span
              data-testid={`status-vault-pill-${w.team_id}`}
              className={`inline-flex items-center px-2 py-0.5 border border-theme-border-default font-mono text-[10px] uppercase tracking-widest ${vaultClass}`}
            >
              {w.vault_status}
            </span>
          </div>
          <div className="font-mono text-[10px] text-theme-text-muted mt-0.5">
            team_id: {w.team_id}
          </div>
          {isSuperadmin && (
            <div
              data-testid={`status-owner-${w.team_id}`}
              className="font-mono text-[10px] text-theme-text-tertiary mt-0.5"
            >
              owner: {w.owned_by_username ?? "unknown"}
            </div>
          )}
        </div>
      </div>

      <dl className="mt-4 pt-4 border-t border-theme-border-default grid grid-cols-2 gap-x-6 gap-y-3">
        <TimestampField label="Validated" iso={w.last_validated_at} />
        <TimestampField label="Last synced" iso={w.last_polled_at} />
        <TimestampField label="Last message" iso={w.last_message_at} />
        <NumberField
          label="Backfill"
          value={w.backfill_complete ? "complete" : "in progress"}
        />
        <NumberField label="Messages" value={w.message_count.toLocaleString()} />
        <NumberField
          label="Attachments"
          value={w.attachment_count.toLocaleString()}
        />
      </dl>

      {w.last_slack_error && (
        <div
          data-testid={`status-error-${w.team_id}`}
          className="mt-4 pt-4 border-t border-theme-border-default"
        >
          <div className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary mb-1">
            Last error
          </div>
          <div className="bg-theme-error-bg border border-theme-error-border px-3 py-2 text-xs">
            <div className="font-mono text-theme-error-text">
              {w.last_slack_error.type ?? "error"}
            </div>
            {w.last_slack_error.message && (
              <div className="text-theme-text-secondary mt-0.5">
                {w.last_slack_error.message}
              </div>
            )}
            {w.last_slack_error.at && (
              <div className="font-mono text-[10px] text-theme-text-tertiary mt-1">
                {formatRelative(w.last_slack_error.at)} ·{" "}
                {formatAbsoluteIst(w.last_slack_error.at)}
              </div>
            )}
          </div>
        </div>
      )}
    </article>
  );
}

interface TimestampFieldProps {
  label: string;
  iso: string | null;
}

function TimestampField({ label, iso }: TimestampFieldProps) {
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary">
        {label}
      </dt>
      <dd className="mt-1">
        <div className="font-mono text-xs text-theme-text-secondary">
          {formatRelative(iso)}
        </div>
        {iso && (
          <div className="font-mono text-[10px] text-theme-text-muted">
            {formatAbsoluteIst(iso)}
          </div>
        )}
      </dd>
    </div>
  );
}

interface NumberFieldProps {
  label: string;
  value: string;
}

function NumberField({ label, value }: NumberFieldProps) {
  return (
    <div>
      <dt className="font-mono text-[10px] uppercase tracking-widest text-theme-text-tertiary">
        {label}
      </dt>
      <dd className="mt-1 font-mono text-xs text-theme-text-secondary">
        {value}
      </dd>
    </div>
  );
}
